import { ref, push, onValue, query, orderByChild, startAt, endAt, off, get } from 'firebase/database';
import { database } from '../config/firebase';

export interface UserActivity {
  id: string;
  userId: string;
  type: 'team_joined' | 'design_created' | 'moodboard_created' | 'collaboration_started' | 'design_updated';
  description: string;
  relatedId?: string; // teamId, designId, etc.
  relatedName?: string; // team name, design name, etc.
  timestamp: number;
}

export interface ActivityStats {
  teamsJoined: number;
  myDesigns: number;
  collaborations: number;
}

class ActivityService {
  // Create a new activity
  async createActivity(userId: string, activity: Omit<UserActivity, 'id' | 'userId' | 'timestamp'>): Promise<void> {
    const activitiesRef = ref(database, 'activities');
    await push(activitiesRef, {
      ...activity,
      userId,
      timestamp: Date.now()
    });
  }

  // Get user activities for last 7 days
  async getUserActivities(userId: string, callback: (activities: UserActivity[]) => void): Promise<() => void> {
    const activitiesRef = ref(database, 'activities');
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    const activitiesQuery = query(
      activitiesRef,
      orderByChild('timestamp'),
      startAt(sevenDaysAgo)
    );

    const unsubscribe = onValue(activitiesQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const activities: UserActivity[] = Object.entries(data)
          .map(([id, activity]: [string, any]) => ({ id, ...activity }))
          .filter((activity: UserActivity) => activity.userId === userId)
          .sort((a: UserActivity, b: UserActivity) => b.timestamp - a.timestamp)
          .slice(0, 10); // Limit to 10 most recent activities
        
        callback(activities);
      } else {
        callback([]);
      }
    });

    return unsubscribe;
  }

  // Get user stats
  async getUserStats(userId: string, callback: (stats: ActivityStats) => void): Promise<() => void> {
    const teamsRef = ref(database, 'teams');
    const documentsRef = ref(database, 'documents');

    const unsubscribeTeams = onValue(teamsRef, (teamsSnapshot) => {
      const teamsData = teamsSnapshot.val();
      let teamsJoined = 0;
      
      if (teamsData) {
        teamsJoined = Object.values(teamsData).filter((team: any) => 
          team.members && team.members[userId]
        ).length;
      }

      const unsubscribeDocuments = onValue(documentsRef, (documentsSnapshot) => {
        const documentsData = documentsSnapshot.val();
        let myDesigns = 0;
        let collaborations = 0;
        
        if (documentsData) {
          // Count user's own designs
          const userDesigns = Object.values(documentsData).filter((doc: any) => 
            doc.owner_uid === userId
          );
          myDesigns = userDesigns.length;
          
          // Count collaborations (documents where user is a collaborator but not owner)
          const collaborativeDocs = Object.values(documentsData).filter((doc: any) => 
            doc.owner_uid !== userId && doc.collaborators && doc.collaborators[userId]
          );
          collaborations = collaborativeDocs.length;
        }

        callback({
          teamsJoined,
          myDesigns,
          collaborations
        });
      });

      return unsubscribeDocuments;
    });

    return unsubscribeTeams;
  }

  // Helper methods for creating specific activities
  async logTeamJoined(userId: string, teamId: string, teamName: string): Promise<void> {
    await this.createActivity(userId, {
      type: 'team_joined',
      description: 'Joined team',
      relatedId: teamId,
      relatedName: teamName
    });
  }

  async logDesignCreated(userId: string, designId: string, designName: string): Promise<void> {
    await this.createActivity(userId, {
      type: 'design_created',
      description: 'Created design',
      relatedId: designId,
      relatedName: designName
    });
  }

  async logMoodboardCreated(userId: string, boardId: string, boardName: string): Promise<void> {
    await this.createActivity(userId, {
      type: 'moodboard_created',
      description: 'Created board',
      relatedId: boardId,
      relatedName: boardName
    });
  }

  async logDesignUpdated(userId: string, designId: string, designName: string): Promise<void> {
    await this.createActivity(userId, {
      type: 'design_updated',
      description: 'Updated design',
      relatedId: designId,
      relatedName: designName
    });
  }

  async logCollaborationStarted(userId: string, projectId: string, projectName: string): Promise<void> {
    await this.createActivity(userId, {
      type: 'collaboration_started',
      description: 'Started collab',
      relatedId: projectId,
      relatedName: projectName
    });
  }

  // Backfill activities for existing designs that were created before activity logging
  async backfillDesignActivities(userId: string): Promise<void> {
    try {
      // Get existing activities to avoid duplicates
      const existingActivitiesRef = ref(database, 'activities');
      const existingSnapshot = await get(existingActivitiesRef);
      const existingActivities = existingSnapshot.val() || {};
      
      // Get user's existing designs
      const documentsRef = ref(database, 'documents');
      const documentsSnapshot = await get(documentsRef);
      const documentsData = documentsSnapshot.val();
      
      if (!documentsData) return;
      
      const userDesigns = Object.entries(documentsData)
        .filter(([id, doc]: [string, any]) => doc.owner_uid === userId);
      
      for (const [designId, design] of userDesigns) {
        const designData = design as any;
        // Check if activity already exists for this design
        const activityExists = Object.values(existingActivities).some((activity: any) => 
          activity.userId === userId && 
          activity.type === 'design_created' && 
          activity.relatedId === designId
        );
        
        if (!activityExists) {
          // Create activity for existing design
          await this.createActivity(userId, {
            type: 'design_created',
            description: 'Created design',
            relatedId: designId,
            relatedName: designData.name
          });
        }
      }
    } catch (error) {
      console.error('Error backfilling design activities:', error);
    }
  }
}

const activityService = new ActivityService();
export default activityService; 